import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Brain, Sparkles, ShieldCheck, Search, GitBranch, FileText,
  Loader2, AlertTriangle, CheckCircle2, RotateCcw, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABELS = {
  queued: 'Queued',
  analysis: 'Analysing your data',
  research: 'Researching industry benchmarks',
  validation: 'Cross-validating findings',
  recommendation: 'Drafting recommendations',
  synthesis: 'Synthesising executive view',
  completed: 'Complete',
  failed: 'Failed',
};

const AGENT_CARDS = [
  { key: 'analysis',         label: 'Analysis',        Icon: Brain,       accent: 'emerald' },
  { key: 'research',         label: 'Research',        Icon: Search,      accent: 'cyan'    },
  { key: 'validation',       label: 'Validation',      Icon: ShieldCheck, accent: 'violet'  },
  { key: 'recommendations',  label: 'Recommendations', Icon: GitBranch,   accent: 'amber'   },
  { key: 'final_assessment', label: 'Synthesis',       Icon: FileText,    accent: 'rose'    },
];

const ACCENT = {
  emerald: { bg: 'bg-emerald-50',  ring: 'border-emerald-200', text: 'text-emerald-700' },
  cyan:    { bg: 'bg-cyan-50',     ring: 'border-cyan-200',    text: 'text-cyan-700'    },
  violet:  { bg: 'bg-violet-50',   ring: 'border-violet-200',  text: 'text-violet-700'  },
  amber:   { bg: 'bg-amber-50',    ring: 'border-amber-200',   text: 'text-amber-700'   },
  rose:    { bg: 'bg-rose-50',     ring: 'border-rose-200',    text: 'text-rose-700'    },
};

const STATUS_BADGE = {
  not_started: { txt: 'Not started', cls: 'bg-gray-100 text-gray-700' },
  running:     { txt: 'Running…',    cls: 'bg-emerald-100 text-emerald-700' },
  completed:   { txt: 'Completed',   cls: 'bg-emerald-100 text-emerald-700' },
  partial:     { txt: 'Partial',     cls: 'bg-amber-100 text-amber-700' },
  failed:      { txt: 'Failed',      cls: 'bg-red-100 text-red-700' },
};

const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);

const RenderValue = ({ value, depth }) => {
  const d = depth || 0;
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400">—</span>;
  }
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') {
    return <span className="text-gray-800">{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400">—</span>;
    return (
      <ul className="space-y-1.5">
        {value.slice(0, 10).map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
            <div className="flex-1"><RenderValue value={item} depth={d + 1} /></div>
          </li>
        ))}
      </ul>
    );
  }
  if (isObj(value)) {
    return (
      <div className={d > 0 ? 'space-y-1.5 pl-3 border-l border-gray-200' : 'space-y-1.5'}>
        {Object.entries(value).slice(0, 16).map(([k, v]) => (
          <div key={k} className="text-sm">
            <span className="font-medium text-gray-600">{k.replace(/_/g, ' ')}: </span>
            <RenderValue value={v} depth={d + 1} />
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-gray-800">{String(value)}</span>;
};

const AgentSection = ({ Icon, label, accent, payload, testId }) => {
  const c = ACCENT[accent];
  const empty = !payload || (isObj(payload) && Object.keys(payload).length === 0);
  if (empty) {
    return (
      <div className={`rounded-xl border ${c.ring} ${c.bg} p-4`} data-testid={testId}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`w-4 h-4 ${c.text}`} />
          <h4 className={`text-sm font-bold uppercase tracking-wider ${c.text}`}>{label}</h4>
        </div>
        <p className="text-xs text-gray-500">No output yet.</p>
      </div>
    );
  }
  return (
    <div className={`rounded-xl border ${c.ring} bg-white p-5`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-lg ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${c.text}`} />
        </div>
        <h4 className={`text-sm font-bold uppercase tracking-wider ${c.text}`}>{label}</h4>
      </div>
      <div className="max-h-96 overflow-y-auto pr-1">
        <RenderValue value={payload} />
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const m = STATUS_BADGE[status] || STATUS_BADGE.not_started;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${m.cls}`}>
      {status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
      {m.txt}
    </span>
  );
};

const slugifyType = (t) => {
  const s = (t || '').toLowerCase();
  if (s.indexOf('digital') !== -1) return 'digital-maturity';
  if (s.indexOf('ai') !== -1) return 'ai-maturity';
  if (s.indexOf('data') !== -1) return 'data-maturity';
  if (s.indexOf('sustainability') !== -1) return 'sustainability';
  if (s.indexOf('cyber') !== -1) return 'cyber-security';
  if (s.indexOf('business') !== -1 || s.indexOf('bpcm') !== -1 || s.indexOf('change') !== -1) return 'bpcm';
  if (s.indexOf('company') !== -1 || s.indexOf('performance') !== -1 || s.indexOf('kpi') !== -1) return 'company-performance';
  return '';
};

const AGIInsightsTab = ({ assessmentType, assessmentId }) => {
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [triggering, setTriggering] = useState(false);
  const pollRef = useRef(null);

  const slug = slugifyType(assessmentType);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/multi-agent/run-status/${assessmentId}`);
      setRun(res.data);
      setError('');
      return res.data;
    } catch (e) {
      setError((e && e.response && e.response.data && e.response.data.detail) || 'Could not load AGI status');
      return null;
    }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const data = await fetchStatus();
      const s = data && data.status;
      if (s === 'completed' || s === 'failed') {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 5000);
  };

  useEffect(() => {
    if (!assessmentId) return;
    fetchStatus();
    startPolling();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const handleRerun = async () => {
    if (!slug) return;
    setTriggering(true);
    try {
      await axios.post(`${API}/multi-agent/run-on/${slug}/${assessmentId}`);
      await fetchStatus();
      startPolling();
    } catch (e) {
      setError((e && e.response && e.response.data && e.response.data.detail) || 'Could not trigger AGI run');
    } finally {
      setTriggering(false);
    }
  };

  const status = (run && run.status) || 'not_started';
  const stage = (run && run.stage) || 'queued';
  const progress = (run && run.progress != null) ? run.progress : (status === 'running' ? 30 : 0);
  const results = (run && run.results) || {};
  const confidence = run && run.confidence_score;
  const progressVal = Math.min(100, Math.max(2, progress));

  if (!assessmentId) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
        AGI insights become available once your assessment is saved.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="agi-insights-tab">
      <div
        className="rounded-2xl border border-gray-200 bg-gradient-to-r from-gray-900 via-slate-900 to-gray-900 p-6 text-white relative overflow-hidden"
        data-testid="agi-insights-header"
      >
        <div aria-hidden="true" className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-500/15 blur-3xl" />
        <div aria-hidden="true" className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-violet-500/15 blur-3xl" />
        <div className="relative flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-[10px] tracking-[0.25em] font-bold text-emerald-300">AGI MULTI-AGENT INSIGHTS</span>
            </div>
            <h2 className="text-2xl font-bold mb-1">Deep analysis from 5 specialist agents</h2>
            <p className="text-sm text-gray-300">Analysis · Research · Validation · Recommendations · Synthesis — coordinated by an orchestrator agent.</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={status} />
            {(status === 'completed' || status === 'failed') && (
              <button
                onClick={handleRerun}
                disabled={triggering}
                data-testid="agi-rerun-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                {triggering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                Re-run AGI
              </button>
            )}
          </div>
        </div>
        {(status === 'running' || status === 'not_started') && (
          <div className="relative mt-5">
            <div className="flex items-center justify-between mb-2 text-xs text-gray-300">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-300" />
                {STAGE_LABELS[stage] || 'Working…'}
              </span>
              <span>{progressVal}%</span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-all duration-500 ease-out"
                style={{ width: progressVal + '%' }}
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-2">
              This typically takes 2–4 minutes. You can keep using the rest of the report — results will appear here automatically.
            </p>
          </div>
        )}
        {status === 'failed' && (
          <div className="relative mt-4 rounded-lg bg-red-500/15 border border-red-500/30 px-3 py-2 text-sm text-red-200">
            <AlertTriangle className="w-4 h-4 inline mr-1 -mt-0.5" />
            AGI run failed. {run && run.error ? <span className="opacity-80">{run.error}</span> : null}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {status === 'not_started' && (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center">
          <Brain className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-gray-900 mb-1">AGI run not started yet</h3>
          <p className="text-sm text-gray-500 mb-4">Kick off the 5-agent deep analysis for this assessment.</p>
          <button
            onClick={handleRerun}
            disabled={triggering || !slug}
            data-testid="agi-start-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
          >
            {triggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Run AGI Analysis
          </button>
        </div>
      )}

      {(status === 'running' || status === 'completed' || status === 'partial') && (
        <div className="grid lg:grid-cols-2 gap-5">
          {AGENT_CARDS.map((a) => (
            <AgentSection
              key={a.key}
              Icon={a.Icon}
              label={a.label}
              accent={a.accent}
              payload={results[a.key]}
              testId={'agi-section-' + a.key}
            />
          ))}
        </div>
      )}

      {(status === 'completed' || status === 'partial') && (
        <div className="text-xs text-gray-500 text-center pt-2">
          Confidence: <span className="font-semibold text-gray-700">{confidence != null ? Math.round(confidence * 100) + '%' : 'n/a'}</span>
          {' · '}Powered by GPT-6 Astra, Claude Fable 5.1 & Gemini 3.8 Flash via Emergent Universal Key.
        </div>
      )}
    </div>
  );
};

export default AGIInsightsTab;
